import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { KeyRound, AlertTriangle, Check, Phone, CalendarDays } from "lucide-react";
import { format, parseISO, differenceInCalendarDays } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import { getAgencyColorByAgency, AgencyDot } from "@/lib/agencyColors";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LoanReturnAlerts = ({ agencies, selectedAgency = "all" }) => {
  const [loans, setLoans] = useState([]);
  const navigate = useNavigate();
  const today = format(new Date(), "yyyy-MM-dd");

  const fetchLoans = async () => {
    try {
      const agencyParam = selectedAgency !== "all" ? `&agency_id=${selectedAgency}` : "";
      const res = await axios.get(`${API}/loans?status_filter=en_cours${agencyParam}`);
      setLoans(res.data.filter((l) => l.end_date <= today));
    } catch {
      setLoans([]);
    }
  };

  useEffect(() => { fetchLoans(); }, [selectedAgency]);

  const markReturned = async (loan) => {
    try {
      await axios.put(`${API}/loans/${loan.id}`, { status: "rendu" });
      toast.success(`${loan.vehicle} marqué comme rendu`);
      fetchLoans();
    } catch {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  if (loans.length === 0) return null;

  const overdue = loans.filter((l) => l.end_date < today);
  const dueToday = loans.filter((l) => l.end_date === today);

  return (
    <div
      className="rounded-xl border p-4 space-y-3 animate-fade-in"
      style={{
        borderColor: overdue.length ? "rgba(239,68,68,0.5)" : "rgba(245,158,11,0.5)",
        background: overdue.length ? "rgba(239,68,68,0.06)" : "rgba(245,158,11,0.06)",
      }}
      data-testid="loan-return-alerts"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <AlertTriangle className={`w-5 h-5 ${overdue.length ? "text-red-400" : "text-amber-400"}`} />
          <p className="font-bold text-white" style={{ fontFamily: "Bebas Neue, sans-serif", letterSpacing: "0.05em" }}>
            VÉHICULES DE PRÊT À RÉCUPÉRER
          </p>
          {overdue.length > 0 && (
            <Badge className="bg-red-600 text-white" data-testid="loan-overdue-count">{overdue.length} en retard</Badge>
          )}
          {dueToday.length > 0 && (
            <Badge className="bg-amber-500 text-black" data-testid="loan-due-today-count">{dueToday.length} aujourd'hui</Badge>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={() => navigate("/planning")} className="text-gray-400 hover:text-[#FEED00] h-8 text-xs" data-testid="loan-alerts-planning-link">
          <CalendarDays className="w-3.5 h-3.5 mr-1" /> Voir le planning
        </Button>
      </div>

      <div className="grid gap-2 md:grid-cols-2">
        {[...overdue, ...dueToday].map((loan) => {
          const agency = agencies.find((a) => a.id === loan.agency_id);
          const color = getAgencyColorByAgency(agency);
          const late = differenceInCalendarDays(new Date(), parseISO(loan.end_date));
          return (
            <div
              key={loan.id}
              className="flex items-center justify-between gap-3 rounded-lg px-3 py-2 border"
              style={{ background: "#0A0A0A", borderColor: late > 0 ? "rgba(239,68,68,0.4)" : "rgba(245,158,11,0.4)" }}
              data-testid={`loan-alert-${loan.id}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <KeyRound className="w-4 h-4 shrink-0" style={{ color: color.text }} />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white truncate">
                    {loan.vehicle}
                    {loan.immatriculation && <span className="font-mono text-gray-500 text-xs ml-1">({loan.immatriculation})</span>}
                  </p>
                  <p className="text-xs text-gray-400 truncate flex items-center gap-2">
                    <span>{loan.client_name}</span>
                    {loan.client_phone && (
                      <a href={`tel:${loan.client_phone}`} className="inline-flex items-center gap-1 hover:text-[#FEED00]" data-testid={`loan-alert-call-${loan.id}`}>
                        <Phone className="w-3 h-3" />{loan.client_phone}
                      </a>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <div className="text-right hidden sm:block">
                  <p className="text-xs flex items-center justify-end gap-1" style={{ color: color.text }}>
                    <AgencyDot color={color} className="w-2 h-2" />{agency?.city || "—"}
                  </p>
                  <p className={`text-xs font-medium ${late > 0 ? "text-red-400" : "text-amber-400"}`}>
                    {late > 0 ? `${late} jour${late > 1 ? "s" : ""} de retard` : `Retour prévu aujourd'hui`}
                    <span className="text-gray-600"> · {format(parseISO(loan.end_date), "d MMM", { locale: fr })}</span>
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={() => markReturned(loan)}
                  className="h-8 text-xs bg-green-600 hover:bg-green-700 text-white"
                  data-testid={`loan-alert-return-${loan.id}`}
                >
                  <Check className="w-3 h-3 mr-1" /> Rendu
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
